import axios from 'axios';
import { getManifestUrlForVersionAlias, VERSION_ALIAS_VALUE_DISABLED } from './getManifestUrlForVersionAlias';
// Yields the base path for a given URL
const basePathRegex = /^.*\//;
let assetsLoaded = {};
/**
 * Loads the assets for a nab-x custom element app and inserts it in to the DOM
 *
 * @todo only load css for the entry point
 */
export class XV2AppLoader {
    constructor(config) {
        this.config = config;
        this.assetEls = [];
    }
    // Override assetManifestUrl based on versionAlias if supplied
    get assetManifestUrl() {
        if (this.config.appMetaData.versionAlias && this.config.appMetaData.versionAlias !== 'NON-EXISTENT') {
            return getManifestUrlForVersionAlias(this.config.appMetaData.assetManifestUrl, this.config.appMetaData.versionAlias, this.config.appMetaData.id // this could also use web component tag or name or Id
            );
        }
        return this.config.appMetaData.assetManifestUrl;
    }
    get instanceId() {
        return this.config.appConfig.instanceId;
    }
    get appId() {
        return this.config.appMetaData.id;
    }
    get hostElement() {
        return this.config.hostElement;
    }
    /**
     * Load the instance
     */
    load() {
        if (this.unloadPromise) {
            return Promise.reject(new Error(`App ${this.appId} (instance ${this.instanceId}) can not be started as it's already stopping`));
        }
        // Ensure we only invoke the start process once
        if (this.loadPromise) {
            return this.loadPromise;
        }
        // eslint-disable-next-line no-async-promise-executor
        this.loadPromise = new Promise(async (resolve, reject) => {
            try {
                if (this.assetManifestUrl === VERSION_ALIAS_VALUE_DISABLED) {
                    return reject(new Error(`App ${this.appId} (instance ${this.instanceId}) can not be started as it has been disabled in the shell`));
                }
                // Configure public path config for app
                this.configurePublicPath();
                // Create the custom element for the app
                this.appElement = this.createAppElement();
                // Apply props to the element
                this.applyProps(this.config);
                // Apply custom attributes to the element
                this.applyCustomAttributes();
                /**
                 * Insert it in to the DOM - We do this before loading assets as some apps in the wild are silly enough
                 * to be attempting a querySelector on their own element as soon as the bundle is loaded
                 */
                if (this.config.hostElement.shadowRoot) {
                    this.config.hostElement.shadowRoot.appendChild(this.appElement);
                }
                else {
                    this.config.hostElement.appendChild(this.appElement);
                }
                // Load assets & create element in parallel
                await this.loadAssets();
                if (this.config.authConfig.requestTokenOnLoad) {
                    // Kick off auth process but don't wait for it
                    this.initAuth();
                }
                resolve();
            }
            catch (e) {
                this.removePublicPath();
                return reject(new Error(`Failed to start application ${this.appId} (instance ${this.instanceId}): \n${(e && e.message) || e}`));
            }
        });
        return this.loadPromise;
    }
    /**
     * This kicks off the authentication/token exchange process but doesn't wait for it
     *
     * The app requests its token using the supplied authenticate() action function which returns a promise
     * - Promise will resolve immediately with the cached token if the exchange is complete before the app first requests it
     * - Promise will resolve as soon as the token exchange is complete if the exchange is still in progress
     * - In all other cases (eg. expired), the exchange will be kicked off again
     */
    initAuth() {
        if (this.config.actions && this.config.actions.authenticate) {
            this.config.actions.authenticate().catch(() => { }); // Swallow rejection as we don't care about the result
        }
    }
    /**
     * Stop the instance
     *
     * @todo Obtain closing state of application and include it in promise resolution
     * @todo cleanup mechanism for styles-components om app stop
     */
    unload() {
        // Ensure stop process is only invoked once
        if (this.unloadPromise) {
            return this.unloadPromise;
        }
        if (!this.loadPromise) {
            return Promise.reject(new Error(`App ${this.appId} (instance ${this.instanceId}) can't be stopped as it has not started`));
        }
        // eslint-disable-next-line no-async-promise-executor
        this.unloadPromise = new Promise(async (resolve, reject) => {
            // Wait for start process to complete before attempting to stop the app
            try {
                await this.loadPromise;
            }
            catch (e) {
                // Do nothing, we don't need the app to have started successfully in order to stop it!
            }
            // Remove element from DOM - This will invoke the element's disconnectedCallback
            if (this.appElement) {
                // @todo this yields "Warning: unmountComponentAtNode(): The node you're attempting to unmount was rendered by another copy of React" Is there a before disconnect callback in LitElement?
                // Remove app element from DOM
                this.appElement.remove();
            }
            // Remove script elements from the DOM (It's not necessary to remove scripts as they can't be unloaded from memory anyway)
            this.assetEls
                .filter((el) => el.tagName === 'LINK')
                .forEach((el) => {
                el.remove();
            });
            this.removePublicPath();
            resolve();
        });
        return this.unloadPromise;
    }
    /**
     * Update appData
     */
    async update(config) {
        if (!this.loadPromise) {
            return Promise.reject(new Error(`App ${this.appId} (instance ${this.instanceId}) can't be updated as it has not started`));
        }
        await this.loadPromise;
        this.applyProps(config); // Apply updated props
    }
    /**
     * Create & attach script & link tags for all .js and .css files in the app's asset-manifest
     */
    async loadAssets() {
        const assetList = await this.getAssetList();
        const base = basePathRegex.exec(this.assetManifestUrl);
        await Promise.all(assetList
            .filter((f) => {
            const fileType = f.substring(f.lastIndexOf('.'));
            return fileType === '.js' || fileType === '.css';
        })
            .map((f) => {
            // Use exact path from manifest where it's a fully qualified url, otherwise prepend base path
            return this.loadAsset(/^(http(s)?:)?\/\//.exec(f) ? f : `${base}${f.replace(/^\//, '')}`);
        }));
    }
    async loadAsset(url) {
        // @todo don't skip loading script assets as they're cleaned up on stop
        if (assetsLoaded[url]) {
            return;
        }
        await new Promise((resolve, reject) => {
            const el = this.createAssetElement(url);
            el.addEventListener('load', () => {
                if (!url.match(/\.css$/)) {
                    // Don't cache css files as we'll remove
                    assetsLoaded[url] = true;
                }
                resolve();
            });
            el.addEventListener('error', () => {
                reject();
            });
            this.attachAssetElement(el);
        });
    }
    getExt(filename) {
        return filename.slice(filename.lastIndexOf('.') + 1);
    }
    /**
     * Create a script or link element
     */
    createAssetElement(url) {
        const ext = this.getExt(url);
        let el;
        if (ext === 'css') {
            /**
             * @todo need to attach styles inside this.appElement and see if it gets slotted in by lit-html
             *
             *
             * Most widely compatible syntax seems to be:
             *
             *
             * <style> @import "css/generalStyle.css"; </style>
             *
             * First potential solution:
             * const style = this.appElement.ownerDocument.createElement('style');
             * style.innerText = `@import "css/generalStyle.css"`;
             * this.appElement.prepend(style);
             *
             * Once the element is connected and rendered the style tag should be slotted in to the shadowDOM
             * however it will be in the light DOM until lit-html slots it meaning there may be a blink where styling
             * is applied to the host document before it's slotted. Worst case scenario styles will remain
             * in the host document after slotting
             *
             * Alternate solution:
             *
             * const style = this.appElement.ownerDocument.createElement('style');
             * style.innerText = `@import "css/generalStyle.css"`;
             * await customElements.whenDefined(this.config.appMetaData.tag);
             * this.appElement.shadowRoot.prepend(style);
             *
             */
            el = this.config.hostElement.ownerDocument.createElement('link');
            el.setAttribute('rel', 'stylesheet');
            el.setAttribute('type', 'text/css');
            el.setAttribute('href', url);
        }
        else {
            el = this.config.hostElement.ownerDocument.createElement('script');
            el.setAttribute('type', 'text/javascript');
            el.setAttribute('src', url);
        }
        return el;
    }
    /**
     * Attach a script or link element to the DOM
     */
    attachAssetElement(el) {
        if (el.tagName === 'SCRIPT') {
            this.config.hostElement.ownerDocument.body.appendChild(el);
        }
        else {
            this.config.hostElement.ownerDocument.head.appendChild(el);
        }
        this.assetEls.push(el);
    }
    /**
     * Apply config, data, actions etc to the custom element as props
     */
    applyProps(config) {
        // In the case of instanceId, attach it as an attribute to help with automation testing
        if (this.instanceId) {
            this.appElement.setAttribute('x-instance-id', this.instanceId);
        }
        if (this.appId) {
            this.appElement.setAttribute('x-app-id', this.appId);
        }
        let propsApplied;
        // Apply props from app registry config
        if (config.appMetaData && config.appMetaData.properties) {
            propsApplied = this.applyCustomProperties(config);
        }
        // Inject appConfig as a prop
        if (config.appConfig && (!propsApplied || (propsApplied.indexOf('appConfig') === -1 && propsApplied.indexOf('appconfig') === -1))) {
            this.appElement.appConfig = config.appConfig;
        }
        // Inject data via the appData prop if supplied
        if (config.appData) {
            this.appElement.appData = config.appData;
        }
        this.appElement.params = config.params || this.appElement.params || {};
        // Inject authConfig as a prop
        if (config.authConfig) {
            this.appElement.authConfig = config.authConfig;
        }
        // Inject appState via the appData prop if supplied
        if (config.appState) {
            this.appElement.appState = config.appState;
        }
        // Inject actions via the actions prop
        if (config.actions) {
            this.appElement.actions = config.actions;
        }
        // Inject user via the user prop
        if (config.user) {
            this.appElement.user = config.user;
        }
    }
    applyCustomProperties(config) {
        const propsApplied = [];
        for (const propName of Object.keys(config.appMetaData.properties)) {
            propsApplied.push(propName);
            if (propName.toLowerCase() === 'isolation') {
                this.appElement.setAttribute('isolation', `${config.appMetaData.properties.isolation}`);
            }
            if (propName.toLowerCase() === 'appconfig') {
                if (typeof config.appMetaData.properties[propName] === 'object') {
                    this.appElement.appConfig = {
                        ...config.appConfig,
                        ...config.appMetaData.properties[propName]
                    };
                }
                else {
                    this.appElement.setAttribute(propName, config.appMetaData.properties[propName]);
                }
            }
            else {
                this.appElement[propName] = config.appMetaData.properties[propName];
            }
        }
        return propsApplied;
    }
    applyCustomAttributes() {
        if (this.config.appMetaData.attributes) {
            for (const attName of Object.keys(this.config.appMetaData.attributes)) {
                if (attName.toLowerCase() === 'class') {
                    this.appElement.classList.add(...this.config.appMetaData.attributes[attName].toString().split(' '));
                }
                else {
                    this.appElement.setAttribute(attName, this.config.appMetaData.attributes[attName].toString());
                }
            }
        }
    }
    /**
     * Create custom element using the configured tagName
     */
    createAppElement() {
        return this.config.hostElement.ownerDocument.createElement(this.config.appMetaData.tag);
    }
    /**
     * Get list of .js and .css files to load for the app
     */
    async getAssetList() {
        let manifest;
        let rawManifest;
        if (this.assetManifestUrl) {
            rawManifest = await this.loadAssetManifest(this.assetManifestUrl);
        }
        else {
            return []; // In this case we assume this app is bundled with the SPA or runtime and can be loaded without loading additional assets
        }
        // Standardise manifest format
        if (rawManifest && typeof rawManifest === 'object' && rawManifest.files && typeof rawManifest.files === 'object') {
            manifest = rawManifest.files;
        }
        else if (typeof rawManifest !== 'object' || !rawManifest) {
            throw new Error(`Invalid asset manifest`);
        }
        else {
            manifest = rawManifest;
        }
        const entry = this.config.appMetaData.entryPoint || 'main.js';
        const entryBase = entry.replace(/\.[^.]+$/, '');
        if (!manifest[`${entryBase}.js`]) {
            throw new Error(`Entry point ${entry} not defined in asset manifest for app ${this.config.appMetaData.id}`);
        }
        // Create an array containing only the file names of css files and the js entry point
        const files = [];
        if (manifest[`${entryBase}.css`]) {
            files.push(manifest[`${entryBase}.css`]);
        }
        files.push(manifest[`${entryBase}.js`]);
        return files;
    }
    /**
     * Retrieve the asset manifest for the app
     *
     * @todo cache manifests
     */
    async loadAssetManifest(url) {
        try {
            return (await axios.get(url)).data;
        }
        catch (e) {
            throw new Error(`Failed to retrieve asset manifest from ${url}. HTTP status: ${e}`);
        }
    }
    /**
     * Configures public path for the app without leaving any side effects on window
     */
    configurePublicPath() {
        const window = this.config.hostElement.ownerDocument.defaultView;
        const { assetManifestUrl } = this;
        window.xPublicPaths = window.xPublicPaths || {};
        const removePublicPath = () => this.removePublicPath();
        Object.defineProperty(window.xPublicPaths, this.config.appMetaData.id, {
            get: function getPublicPath() {
                try {
                    return (assetManifestUrl && basePathRegex.exec(assetManifestUrl)[0]) || window.location.origin.replace(/(?!\/)$/, '/');
                }
                finally {
                    removePublicPath();
                }
            },
            configurable: true,
            enumerable: true
        });
    }
    removePublicPath() {
        const window = this.config.hostElement.ownerDocument.defaultView;
        const appId = this.config.appMetaData.id;
        if (window.xPublicPaths) {
            delete window.xPublicPaths[appId];
            if (!Object.keys(window.xPublicPaths).length) {
                delete window.xPublicPaths;
            }
        }
    }
}
export function clearCache() {
    assetsLoaded = {};
}
//# sourceMappingURL=XV2AppLoader.js.map